import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BookingProvider } from "./context/BookingContext";
import { Toaster } from "./components/ui/toaster";

// Components
import LandingPage from "./components/LandingPage";
import LoginPage from "./components/LoginPage";
import AboutUsPage from "./components/AboutUsPage";
import ContactUsPage from "./components/ContactUsPage";
import UserDashboard from "./components/user/UserDashboard";
import BookingHistory from "./components/user/BookingHistory";
import Invoice from "./components/user/Invoice";
import FASTagRecharge from "./components/user/FASTagRecharge";
import ProfilePage from "./components/user/ProfilePage";
import ReferralPage from "./components/user/ReferralPage";
import DriverDashboard from "./components/driver/DriverDashboard";
import ActiveTrip from "./components/driver/ActiveTrip";
import DriverOnboarding from "./components/driver/DriverOnboarding";
import DriverRegistration from "./components/driver/DriverRegistration";
import DriverLoginPage from "./components/driver/DriverLoginPage";
import DriverTripHistory from "./components/driver/DriverTripHistory";
import DriverEarnings from "./components/driver/DriverEarnings";
import BankDetailsPage from "./components/driver/BankDetailsPage";
import ChangePasswordPage from "./components/driver/ChangePasswordPage";
import AdminLogin from "./components/admin/AdminLogin";
import AdminDashboard from "./components/admin/AdminDashboard";
import AdminDrivers from "./components/admin/AdminDrivers";
import AdminBookings from "./components/admin/AdminBookings";
import AdminTransactions from "./components/admin/AdminTransactions";
import AdminPayouts from "./components/admin/AdminPayouts";

// Protected Route Component
const ProtectedRoute = ({ children, requiredType }) => {
  const { isAuthenticated, userType, loading } = useAuth();

  if (loading) return null;

  if (!isAuthenticated) {
    const to = requiredType === 'admin' ? '/admin/login' : '/login';
    return <Navigate to={to} replace />;
  }

  if (requiredType && userType !== requiredType) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/contact" element={<ContactUsPage />} />
      
      {/* User Routes */}
      <Route
        path="/user/dashboard"
        element={
          <ProtectedRoute requiredType="user">
            <UserDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user/profile"
        element={
          <ProtectedRoute requiredType="user">
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user/referral"
        element={
          <ProtectedRoute requiredType="user">
            <ReferralPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user/history"
        element={
          <ProtectedRoute requiredType="user">
            <BookingHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user/invoice/:bookingId"
        element={
          <ProtectedRoute requiredType="user">
            <Invoice />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user/fastag"
        element={
          <ProtectedRoute requiredType="user">
            <FASTagRecharge />
          </ProtectedRoute>
        }
      />

      {/* Driver Routes */}
      <Route path="/driver/register" element={<DriverRegistration />} />
      <Route path="/driver/login" element={<DriverLoginPage />} />
      <Route
        path="/driver/onboarding"
        element={
          <ProtectedRoute requiredType="driver">
            <DriverOnboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver/dashboard"
        element={
          <ProtectedRoute requiredType="driver">
            <DriverDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver/history"
        element={
          <ProtectedRoute requiredType="driver">
            <DriverTripHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver/earnings"
        element={
          <ProtectedRoute requiredType="driver">
            <DriverEarnings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver/active-trip"
        element={
          <ProtectedRoute requiredType="driver">
            <ActiveTrip />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver/bank-details"
        element={
          <ProtectedRoute requiredType="driver">
            <BankDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver/change-password"
        element={
          <ProtectedRoute requiredType="driver">
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute requiredType="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/drivers"
        element={
          <ProtectedRoute requiredType="admin">
            <AdminDrivers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/bookings"
        element={
          <ProtectedRoute requiredType="admin">
            <AdminBookings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/transactions"
        element={
          <ProtectedRoute requiredType="admin">
            <AdminTransactions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payouts"
        element={
          <ProtectedRoute requiredType="admin">
            <AdminPayouts />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <BookingProvider>
            <AppRoutes />
            <Toaster />
          </BookingProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
